<script setup lang="ts">
import { computed } from 'vue';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useFilterOptions } from '@/composables/api/useFilterOptions';
import type { CreateField } from '@/components/createRecord/createRecordForms';

/**
 * One "pick from a short list" field in a create form. The list is either fixed,
 * such as a status, or the records of a small model, such as brands, fetched once.
 */
const props = defineProps<{ field: CreateField; inputId: string }>();
const modelValue = defineModel<string | number | boolean | null>({ required: true });

const fetched = useFilterOptions({ slug: props.field.optionsSlug ?? '', enabled: Boolean(props.field.optionsSlug) });

const choices = computed(() => {
    if (props.field.options) {
        return props.field.options.map((option) => ({ id: option.id, label: option.display_string }));
    }
    return (fetched.data.value ?? []).map((option) => ({ id: option.id, label: option.display_string ?? option.name ?? `#${option.id}` }));
});

const selected = computed({
    get: () => (modelValue.value === null ? undefined : String(modelValue.value)),
    set: (value: string | undefined) => {
        modelValue.value = value === undefined ? null : Number(value);
    },
});
</script>

<template>
  <Select v-model="selected">
    <SelectTrigger :id="inputId" class="w-full" :data-testid="`create-field-${field.name}`">
      <SelectValue :placeholder="fetched.isLoading.value ? 'Loading…' : 'Choose…'" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem v-for="choice in choices" :key="choice.id" :value="String(choice.id)">{{ choice.label }}</SelectItem>
    </SelectContent>
  </Select>
</template>
